import React, { useState, useMemo, useEffect, useRef } from 'react';
import { TimeEntry, WorkRolesDict, WorkTypesDict, ChargeCodesDict, WorkRoleValidationsList, PatchOperation } from '../types/api.types';
import { apiService, BadRequestError } from '../services/apiService';
import { useData } from '../context/DataContext';
import {
  getUserTimezone,
  getTimezoneLabel,
  utcToLocalTime,
  localToUtcIso,
  calculateHoursDiff,
  extractDatePortion,
} from '../utils/timeUtils';
import ChargeCodeEditModal from './ChargeCodeEditModal';
import './TimeEntryRow.css';

interface TimeEntryRowProps {
  entry: TimeEntry;
  workRoles: WorkRolesDict;
  workTypes: WorkTypesDict;
  chargeCodes: ChargeCodesDict;
  workRoleValidations: WorkRoleValidationsList;
  onDelete: (id: number) => void;
  onUpdate: () => void | Promise<void>;
  onCopyFrom: (entry: TimeEntry) => void;
  sheetStatus?: string;
  timeDetailSignal?: { action: 'show' | 'hide'; epoch: number };
}

const TimeEntryRow: React.FC<TimeEntryRowProps> = ({
  entry,
  workRoles,
  workTypes,
  chargeCodes,
  workRoleValidations,
  onDelete,
  onUpdate,
  onCopyFrom,
  sheetStatus,
  timeDetailSignal,
}) => {
  const { cwUsersByMemberId } = useData();
  const userTz = useMemo(() => getUserTimezone(), []);
  const tzLabel = useMemo(() => getTimezoneLabel(userTz), [userTz]);

  const departmentName = useMemo(() => {
    const memberId = entry.member?.id;
    if (memberId != null) {
      const user = cwUsersByMemberId[memberId];
      if (user?.defaultDepartment?.name) return user.defaultDepartment.name;
    }
    return '';
  }, [entry.member?.id, cwUsersByMemberId]);

  const [entryDate, setEntryDate] = useState(extractDatePortion(entry.timeStart) || '');
  const [startTime, setStartTime] = useState(utcToLocalTime(entry.timeStart, getUserTimezone()));
  const [endTime, setEndTime] = useState(utcToLocalTime(entry.timeEnd, getUserTimezone()));
  const [deduct, setDeduct] = useState(entry.hoursDeduct ? entry.hoursDeduct.toString() : '');
  const [showTimeDetail, setShowTimeDetail] = useState(false);
  const [actualHours, setActualHours] = useState(
    parseFloat(entry.actualHours.toString()).toString()
  );
  const [selectedWorkRoleId, setSelectedWorkRoleId] = useState(entry.workRole?.id ?? 0);
  const [selectedWorkTypeId, setSelectedWorkTypeId] = useState(entry.workType?.id ?? 0);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isChargeEditOpen, setIsChargeEditOpen] = useState(false);
  const skipNextCalcRef = useRef(true);

  // Sync local state when the entry prop changes (e.g. after a refresh)
  useEffect(() => {
    skipNextCalcRef.current = true;
    setEntryDate(extractDatePortion(entry.timeStart) || '');
    setStartTime(utcToLocalTime(entry.timeStart, userTz));
    setEndTime(utcToLocalTime(entry.timeEnd, userTz));
    setDeduct(entry.hoursDeduct ? entry.hoursDeduct.toString() : '');
    setActualHours(parseFloat(entry.actualHours.toString()).toString());
    setSelectedWorkRoleId(entry.workRole?.id ?? 0);
    setSelectedWorkTypeId(entry.workType?.id ?? 0);
  }, [entry, userTz]);

  // React to external time detail toggle signal
  const lastTimeDetailEpochRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!timeDetailSignal || timeDetailSignal.epoch === lastTimeDetailEpochRef.current) return;
    lastTimeDetailEpochRef.current = timeDetailSignal.epoch;
    setShowTimeDetail(timeDetailSignal.action === 'show');
  }, [timeDetailSignal]);

  // Work role / work type validation
  const validationError = useMemo(() => {
    const roleId = selectedWorkRoleId;
    const typeId = selectedWorkTypeId;
    if (!roleId || !typeId) return null;
    const roleName = workRoles[roleId]?.name;
    const typeName = workTypes[typeId]?.name;
    if (!roleName || !typeName) return null;
    const combo = `${roleName} ${typeName}`;
    console.log(`[Validation] Entry #${entry.id} — comparing "${combo}" against list:`, workRoleValidations);
    if (workRoleValidations.includes(combo)) return null;
    return `"${typeName}" is not a valid work type for "${roleName}"`;
  }, [selectedWorkRoleId, selectedWorkTypeId, workRoleValidations, workRoles, workTypes, entry.id]);

  // Union-type validation: highlight rows based on hours / day-of-week
  const unionWarning = useMemo<'red' | 'orange' | null>(() => {
    const memberId = entry.member?.id;
    if (memberId == null) return null;
    const user = cwUsersByMemberId[memberId];
    const typeName = user?.type?.name?.toLowerCase() ?? '';
    if (!typeName.includes('union')) return null;

    // Red: entry date falls on Saturday or Sunday
    if (entryDate) {
      const d = new Date(entryDate + 'T00:00:00');
      const day = d.getDay(); // 0 = Sunday, 6 = Saturday
      if (day === 0 || day === 6) return 'red';
    }

    // Orange: hours over 8
    if (parseFloat(actualHours) > 8) return 'orange';

    return null;
  }, [entry.member?.id, cwUsersByMemberId, entryDate, actualHours]);

  // Check if any values have changed
  const hasChanges = useMemo(() => {
    const origDate = extractDatePortion(entry.timeStart) || '';
    const origStartTime = utcToLocalTime(entry.timeStart, userTz);
    const origEndTime = utcToLocalTime(entry.timeEnd, userTz);
    return (
      entryDate !== origDate ||
      startTime !== origStartTime ||
      endTime !== origEndTime ||
      parseFloat(actualHours) !== entry.actualHours ||
      selectedWorkRoleId !== (entry.workRole?.id ?? 0) ||
      selectedWorkTypeId !== (entry.workType?.id ?? 0)
    );
  }, [entryDate, startTime, endTime, actualHours, selectedWorkRoleId, selectedWorkTypeId, entry, userTz]);

  // Build chargeTo display
  const chargeToDisplay = useMemo(() => {
    if (entry.chargeToType === 'ChargeCode') {
      const chargeCode = chargeCodes[entry.chargeToId];
      if (chargeCode) {
        return `${chargeCode.company?.name || 'Unknown'} / ${chargeCode.name}`;
      }
      return `Charge Code #${entry.chargeToId}`;
    } else {
      const parts = [
        entry.company?.name,
        entry.project?.name,
        entry.phase?.name,
        entry.ticket?.summary,
      ].filter(Boolean);
      return parts.join(' / ') || `Ticket #${entry.chargeToId}`;
    }
  }, [entry, chargeCodes]);

  // Auto-calculate actual hours when start/end time or deduct changes
  useEffect(() => {
    if (skipNextCalcRef.current) {
      skipNextCalcRef.current = false;
      return;
    }
    if (startTime && endTime) {
      const diff = calculateHoursDiff(startTime, endTime);
      const deductVal = parseFloat(deduct) || 0;
      const calculated = Math.max(0, diff - deductVal);
      setActualHours(parseFloat(calculated.toFixed(2)).toString());
    }
  }, [startTime, endTime, deduct]);

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setActualHours(value);
    }
  };

  const handleDeductChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setDeduct(value);
    }
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      const operations: PatchOperation[] = [
        { op: 'replace', path: 'actualHours', value: parseFloat(actualHours) },
        { op: 'replace', path: 'workRole/id', value: selectedWorkRoleId },
        { op: 'replace', path: 'workType/id', value: selectedWorkTypeId },
      ];

      if (startTime) {
        const timeStartValue = localToUtcIso(entryDate, startTime, userTz);
        operations.push({ op: 'replace', path: 'timeStart', value: timeStartValue });

        if (showTimeDetail) {
          if (endTime) {
            operations.push({
              op: 'replace',
              path: 'timeEnd',
              value: localToUtcIso(entryDate, endTime, userTz),
            });
          }

          operations.push({
            op: 'replace',
            path: 'hoursDeduct',
            value: deduct ? parseFloat(deduct) : 0,
          });
        } else {
          // Compute endTime = startTime + actualHours, zero out deduct
          const startUtc = new Date(timeStartValue);
          const endUtc = new Date(startUtc.getTime() + parseFloat(actualHours) * 3600_000);
          operations.push({
            op: 'replace',
            path: 'timeEnd',
            value: endUtc.toISOString().replace(/\.\d{3}Z$/, 'Z'),
          });
          operations.push({ op: 'replace', path: 'hoursDeduct', value: 0 });
        }
      }

      await apiService.updateTimeEntry(entry.id, operations);
      await onUpdate();
    } catch (error) {
      console.error('Failed to update entry:', error);
      if (error instanceof BadRequestError) {
        alert(error.message);
      } else {
        alert('Failed to update entry');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }
    setIsDeleting(true);
    try {
      await apiService.deleteTimeEntry(entry.id);
      onDelete(entry.id);
    } catch (error) {
      console.error('Failed to delete entry:', error);
      alert('Failed to delete entry');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <tr className={`time-entry-row${validationError ? ' validation-error' : ''}${unionWarning === 'red' ? ' union-red' : unionWarning === 'orange' ? ' union-orange' : ''}`}>
        <td className="cell-date">
          <input
            type="date"
            className="date-input"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
          />
        </td>
        <td className="cell-charge-to">
          <span
            className="charge-to-link"
            onClick={() => setIsChargeEditOpen(true)}
            title="Click to change charge code"
          >
            {chargeToDisplay}
          </span>
        </td>
        <td className="cell-hours">
          <div className="hours-cell-wrapper">
            <input
              type="text"
              className="hours-input"
              value={actualHours}
              onChange={handleHoursChange}
              inputMode="decimal"
            />
            <button
              className={`time-toggle-btn${showTimeDetail ? ' active' : ''}`}
              onClick={() => setShowTimeDetail(!showTimeDetail)}
              title="Toggle time detail"
            >
              ⏱
            </button>
          </div>
        </td>
      <td className="cell-department">
          <span>{departmentName}</span>
        </td>
      <td className="cell-workrole">
        <select
          className="dropdown-select"
          value={selectedWorkRoleId}
          onChange={(e) => setSelectedWorkRoleId(parseInt(e.target.value, 10))}
        >
          <option value={0}>-- Select --</option>
          {Object.values(workRoles).sort((a, b) => a.name.localeCompare(b.name)).map((role) => (
            <option key={role.id} value={role.id}>
              {role.name}
            </option>
          ))}
        </select>
      </td>
      <td className="cell-worktype">
        <select
          className="dropdown-select"
          value={selectedWorkTypeId}
          onChange={(e) => setSelectedWorkTypeId(parseInt(e.target.value, 10))}
        >
          <option value={0}>-- Select --</option>
          {Object.values(workTypes).sort((a, b) => a.name.localeCompare(b.name)).map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
      </td>
      <td className="cell-actions">
        {hasChanges && (
          <button
            className="btn btn-primary btn-small"
            onClick={handleUpdate}
            disabled={isUpdating}
          >
            {isUpdating ? 'Saving...' : 'Update'}
          </button>
        )}
        <button
          className="btn btn-secondary btn-small"
          onClick={() => onCopyFrom(entry)}
        >
          Copy from...
        </button>
        <button
          className="btn btn-danger btn-small"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </button>
      </td>
        <ChargeCodeEditModal
          isOpen={isChargeEditOpen}
          onClose={() => setIsChargeEditOpen(false)}
          onSave={onUpdate}
          entry={entry}
          chargeCodes={chargeCodes}
        />
      </tr>
      {showTimeDetail && (
        <tr className="time-detail-row">
          <td colSpan={7}>
            <div className="time-detail-content">
              <div className="time-detail-field">
                <label>Start Time ({tzLabel})</label>
                <input
                  type="time"
                  className="time-input"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div className="time-detail-field">
                <label>End Time ({tzLabel})</label>
                <input
                  type="time"
                  className="time-input"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
              <div className="time-detail-field">
                <label>Deduct (hrs)</label>
                <input
                  type="text"
                  className="deduct-input"
                  value={deduct}
                  onChange={handleDeductChange}
                  placeholder="0.00"
                  inputMode="decimal"
                />
              </div>
            </div>
          </td>
        </tr>
      )}
      {validationError && (
        <tr className="validation-error-row">
          <td colSpan={7}>
            <span className="validation-error-message">⚠ {validationError}</span>
          </td>
        </tr>
      )}
    </>
  );
};

export default TimeEntryRow;
